/**
 * Константы и помощники для двухуровневой алфавитной навигации в каталоге.
 *
 * Структура:
 *  - Top-level (верхний уровень): 32 русские буквы (а..я, без ё) + 'A-Z' + '0-9'.
 *    На этих страницах рендерится DigramNavigation (сетка двухбуквенных сочетаний).
 *  - Второй уровень: двухбуквенные/односимвольные комбинации (напр. 'аб', 'аё', 'A', '5').
 *    На этих страницах рендерится SkuList — карточки SKU.
 *
 * Backend (GET /api/v1/public/products/skus) поддерживает параметр `letter` любой длины,
 * поэтому фронт может фильтровать по любому префиксу без дополнительных изменений.
 */

/** Русские буквы а..я (32 шт., без ё). Используется для верхнего уровня. */
export const RU_LETTERS: readonly string[] = (() => {
  const arr: string[] = [];
  for (let i = 1072; i <= 1103; i++) arr.push(String.fromCharCode(i));
  return Object.freeze(arr);
})();

/** Русские буквы а..я включая ё (33 шт.). Используется как «вторая буква» в digram-сочетаниях. */
export const RU_LETTERS_WITH_YO: readonly string[] = (() => {
  const arr: string[] = [];
  // а..е (1072..1077)
  for (let i = 1072; i <= 1077; i++) arr.push(String.fromCharCode(i));
  // ё (1105)
  arr.push(String.fromCharCode(1105));
  // ж..я (1078..1103)
  for (let i = 1078; i <= 1103; i++) arr.push(String.fromCharCode(i));
  return Object.freeze(arr);
})();

/** Латинские буквы A..Z. */
export const LATIN_LETTERS: readonly string[] = (() => {
  const arr: string[] = [];
  for (let i = 65; i <= 90; i++) arr.push(String.fromCharCode(i));
  return Object.freeze(arr);
})();

/** Цифры 0..9. */
export const DIGITS: readonly string[] = Object.freeze(['0','1','2','3','4','5','6','7','8','9']);

/** Токены верхнего уровня (страницы с digram-навигацией). */
export const TOP_LEVEL_TOKENS: readonly string[] = Object.freeze([
  ...RU_LETTERS,
  'A-Z',
  '0-9',
]);

/** Максимум digram-сочетаний на одной странице. Порог подобран так, что любой набор
 *  сочетаний (максимум 33 = RU_LETTERS_WITH_YO) помещается на одной странице;
 *  пагинация остаётся как защита от роста набора.
 */
export const DIGRAMS_PER_PAGE = 33;


/**
 * Капитализирует первую букву токена, оставляя остальные без изменения.
 * Примеры: 'аб' → 'Аб', 'амо' → 'Амо', 'A' → 'A', 'A-Z' → 'A-Z', '0-9' → '0-9'.
 * Используется для отображения буквенного индекса на страницах каталога:
 *   заголовок страницы, хлебные крошки, meta-описание.
 * Возвращает пустую строку для пустого токена.
 */
export function capitalizeFirst(token: string): string {
  if (!token) return '';
  return token.charAt(0).toUpperCase() + token.slice(1);
}

/** Является ли `token` токеном верхнего уровня (а..я без ё, 'A-Z', '0-9'). */
export function isTopLevelLetter(token: string): boolean {
  return (TOP_LEVEL_TOKENS as readonly string[]).includes(token);
}

/**
 * Генерирует двухбуквенные (или односимвольные) сочетания для заданного токена верхнего уровня.
 * - Для русской буквы: `letter + secondLetter` для каждой из 33 букв (а..я incl. ё).
 * - Для 'A-Z': латинские буквы A..Z (односимвольные, т.к. эти страницы сами по себе — уровень SKU).
 * - Для '0-9': цифры 0..9.
 * - Для невалидного токена: пустой массив.
 */
export function getDigramsForTopLevel(token: string): readonly string[] {
  if (RU_LETTERS.includes(token)) {
    return RU_LETTERS_WITH_YO.map((second) => `${token}${second}`);
  }
  if (token === 'A-Z') return LATIN_LETTERS;
  if (token === '0-9') return DIGITS;
  return [];
}

/**
 * Определяет, нужно ли подсвечивать заданный токен верхнего уровня
 * как активный для текущей страницы каталога.
 *
 * - `current === token` → точное совпадение (например, /catalog/а/1 → 'а').
 * - Русская буква верхнего уровня активна, если первая буква текущей страницы
 *   совпадает с ней; `ё` нормализуется в `е` (верхнего уровня `ё` нет в панели).
 * - 'A-Z' активна, если текущая страница начинается с латинской буквы (A..Z, a..z).
 * - '0-9' активна, если текущая страница начинается с цифры.
 */
export function isLetterTokenActive(current: string, token: string): boolean {
  if (!current || !token) return false;
  const c = current.toLowerCase();
  const t = token.toLowerCase();
  if (c === t) return true;
  // Русская буква верхнего уровня: сопоставляем первую букву (с нормализацией ё→е).
  if (t.length === 1 && (RU_LETTERS as readonly string[]).includes(t)) {
    const first = c.charCodeAt(0) === 1105 /* ё */ ? 'е' : c.charAt(0);
    return first === t;
  }
  if (t === 'a-z') return /^[a-z]/.test(c);
  if (t === '0-9') return /^[0-9]/.test(c);
  return false;
}
